import { Box, Typography, useTheme, Button, InputBase, MenuItem, TextField } from "@mui/material";
import { useNavigate }  from "react-router-dom";
import { useSelector }  from "react-redux";
import { useState }     from "react";

import BadgeIcon         from "@mui/icons-material/Badge";
import SearchIcon        from "@mui/icons-material/Search";
import AddIcon           from "@mui/icons-material/Add";
import FilterListIcon    from "@mui/icons-material/FilterList";
import VisibilityIcon    from "@mui/icons-material/Visibility";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import FixedSizeDialog   from "components/fixedSizeDialog";
import EmployeeCreateForm from "../forms/employeeForms/employeeCreateForm";
import { useEmployeesList, useDepartmentsList } from "api/hooks/humanResource";
import { URL }           from "api/url";

export default function EmployeeTable({ setIsSuccess }) {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const token              = useSelector((s) => s.authReducer.token);
  const { employeeList, isLoadingEmployeeList, setSearchEmployees, setNewEmployeeData, setDepartmentFilter } = useEmployeesList(1, business_branch_id, token);
  const { departmentList } = useDepartmentsList(1, business_branch_id);

  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState("");

  const inputSx = {
    "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  };

  const columns = [
    { label: "#",               width: 40  },
    { label: "Photo",           width: 60  },
    { label: "Name"                        },
    { label: "Role"                        },
    { label: "Phone"                       },
    { label: "ID No."                      },
    { label: "Department"                  },
    { label: "Next of Kin"                 },
    { label: "Actions",        align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
            <BadgeIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>Employees</Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>{employeeList.length} total employees</Typography>
          </Box>
        </Box>
        <Button onClick={() => setOpen(true)} startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>
          Add Employee
        </Button>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem", p: "0.75rem 1rem", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px" }}>
        <FilterListIcon sx={{ fontSize: "16px", color: accent }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", px: "0.75rem", height: "36px", backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f9f9f9", border: `1px solid ${border}`, borderRadius: "8px", flex: "1 1 180px", maxWidth: "240px" }}>
          <SearchIcon sx={{ fontSize: "16px", color: textSec }} />
          <InputBase placeholder="Search employee…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setSearchEmployees(search); }} sx={{ flex: 1, fontSize: "13px", color: textPri }} />
          <Box onClick={() => setSearchEmployees(search)} sx={{ fontSize: "11px", fontWeight: 500, color: accent, cursor: "pointer" }}>Search</Box>
        </Box>
        <TextField label="Department" size="small" select defaultValue="all" onChange={(e) => setDepartmentFilter(e.target.value)} sx={{ ...inputSx, width: "180px" }}>
          <MenuItem value="all">All departments</MenuItem>
          {departmentList.map((d) => <MenuItem key={d.id} value={d.id}>{d.department_name}</MenuItem>)}
        </TextField>
      </Box>

      <ReportTable columns={columns} rows={employeeList} loading={isLoadingEmployeeList}
        emptyMessage="No employees found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell>
              <Box sx={{ width: "38px", height: "38px", borderRadius: "50%", overflow: "hidden", border: `2px solid ${border}` }}>
                <img alt={row.first_name} src={`${URL}${row.image}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </Box>
            </RCell>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.first_name} {row.last_name}</Typography></RCell>
            <RCell>{row.related_role?.role_name}</RCell>
            <RCell>{row.phone_number}</RCell>
            <RCell>{row.id_number}</RCell>
            <RCell>{row.related_department?.department_name || "—"}</RCell>
            <RCell>
              <Typography sx={{ fontSize: "13px", color: textPri }}>{row.next_of_kin_name}</Typography>
              <Typography sx={{ fontSize: "11px", color: textSec }}>{row.next_of_kin_phone_number}</Typography>
            </RCell>
            <RCell align="center">
              <Box onClick={() => navigate("/employeeDetails", { state: { id: row.id } })} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(29,158,117,0.3)", cursor: "pointer", color: accent, fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}>
                <VisibilityIcon sx={{ fontSize: "14px" }} /> View Details
              </Box>
            </RCell>
          </RRow>
        )}
      />

      <FixedSizeDialog open={open} handleClose={() => setOpen(false)} dialogHeading="Add a New Employee" negativeResponse="Cancel" maxWidth="md"
        content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><EmployeeCreateForm setIsSuccess={setIsSuccess} setOpen={setOpen} setNewEmployeeData={setNewEmployeeData} /></Box>}
      />
    </Box>
  );
}